import React, { useState, useEffect } from 'react';
import { FaTimes, FaPhone, FaLock, FaUser, FaHome, FaMapMarkerAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { sendOTP, verifyOTP, resendOTP, checkUserExists } from '../services/api';

const AuthModal = ({ isOpen, onClose, onAuth, isMandatory }) => {
  const [step, setStep] = useState('phone'); // 'phone', 'otp', or 'details'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [timer, setTimer] = useState(0);
  const [profileForm, setProfileForm] = useState({
    name: '',
    preferredPriceRange: '',
    preferredPropertyType: '',
    preferredBhkConfigurations: [],
    preferredLocation: '',
  });
  const [amenitiesInput, setAmenitiesInput] = useState('');

  // Store temp user data between steps
  const [tempToken, setTempToken] = useState(null);
  // const [tempUser, setTempUser] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setStep('phone');
      setPhone('');
      setOtp('');
      setName('');
      setIsExistingUser(false);
      setError('');
      setTempToken(null);
      setProfileForm({
        name: '',
        preferredPriceRange: '',
        preferredPropertyType: '',
        preferredBhkConfigurations: [],
        preferredLocation: '',
      });
      setAmenitiesInput('');
      // setTempUser(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (phone.length !== 10) {
        setError('Please enter a valid 10-digit mobile number');
        setLoading(false);
        return;
      }

      // Check if user exists
      const checkResult = await checkUserExists(`+91${phone}`);

      if (checkResult.success && checkResult.exists) {
        setIsExistingUser(true);
        setName(checkResult.user.name);
      } else {
        setIsExistingUser(false);
      }

      // Send OTP for both existing and new users
      const otpResult = await sendOTP(`+91${phone}`);
      if (otpResult.success) {
        setStep('otp');
        startTimer();
        if (otpResult.otp) {
          alert(`Dev Mode - OTP: ${otpResult.otp}`);
        }
      } else {
        setError(otpResult.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError(err.message || 'Failed to check user. Please try again.');
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (otp.length !== 6) {
        setError('Please enter a valid 6-digit OTP');
        setLoading(false);
        return;
      }

      // If new user, pass "New User" as name, else use existing name or placeholder
      const verifyName = isExistingUser ? name : "New User";
      const result = await verifyOTP(`+91${phone}`, otp, verifyName);

      if (result.success) {
        localStorage.setItem('userToken', result.token);
        localStorage.setItem('userData', JSON.stringify(result.user));
        
        if (result.isNewUser || !isExistingUser) {
          // It's a new user, proceed to details step
          setTempToken(result.token);
          setProfileForm((prev) => ({
            ...prev,
            name: result.user?.name && result.user.name !== 'New User' ? result.user.name : ''
          }));
          // setTempUser(result.user);
          setStep('details');
        } else {
          // Existing user, complete login
          onAuth(result.user);
          onClose();
        }
      } else {
        setError(result.message || 'Invalid OTP');
      }
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    }
    setLoading(false);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!profileForm.name || profileForm.name.trim() === '') {
      setError('Please enter your name');
      setLoading(false);
      return;
    }

    if (profileForm.name.trim().length < 2) {
      setError('Name must be at least 2 characters');
      setLoading(false);
      return;
    }

    if (!profileForm.preferredPriceRange.trim()) {
      setError('Please select your price range');
      setLoading(false);
      return;
    }

    if (!profileForm.preferredPropertyType.trim()) {
      setError('Please select property type');
      setLoading(false);
      return;
    }

    if (!profileForm.preferredLocation.trim()) {
      setError('Please enter preferred property location');
      setLoading(false);
      return;
    }

    if (profileForm.preferredBhkConfigurations.length === 0) {
      setError('Please select at least one BHK configuration');
      setLoading(false);
      return;
    }

    try {
      const preferredAmenities = amenitiesInput
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

      // Use standard fetch call to hit profile-update using the token we just received
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api'}/user/profile-update`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken || localStorage.getItem('userToken')}`
        },
        body: JSON.stringify({
          name: profileForm.name.trim(),
          preferredPriceRange: profileForm.preferredPriceRange,
          preferredPropertyType: profileForm.preferredPropertyType,
          preferredBhkConfigurations: profileForm.preferredBhkConfigurations,
          preferredLocation: profileForm.preferredLocation.trim(),
          preferredAmenities
        })
      });
      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem('userData', JSON.stringify(data.user));
        onAuth(data.user);
        onClose();
      } else {
        setError(data.message || 'Failed to save profile details');
      }
    } catch (err) {
      setError('Failed to save profile details. Please try again.');
    }
    setLoading(false);
  };

  const bhkOptions = ['1RK', '1BHK', '1.5BHK', '2BHK', '2.5BHK', '3BHK', '3.5BHK', '4BHK', '4.5BHK', '5BHK'];

  const toggleBhkOption = (option) => {
    setProfileForm((prev) => {
      const exists = prev.preferredBhkConfigurations.includes(option);
      return {
        ...prev,
        preferredBhkConfigurations: exists
          ? prev.preferredBhkConfigurations.filter((item) => item !== option)
          : [...prev.preferredBhkConfigurations, option]
      };
    });
    setError('');
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only numbers
    if (value.length <= 10) {
      setPhone(value);
      setError('');
    }
  };

  const handleOTPChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setOtp(value);
      setError('');
    }
  };

  const startTimer = () => {
    setTimer(60);
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOTP = async () => {
    if (timer > 0) return;
    setLoading(true);
    setError('');
    
    try {
      const result = await resendOTP(`+91${phone}`);
      if (result.success) {
        startTimer();
        if (result.otp) {
          alert(`Dev Mode - OTP: ${result.otp}`);
        }
      } else {
        setError(result.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setError('Failed to resend OTP');
    }
    setLoading(false);
  };

  const handleLegalClick = (e, path) => {
    if (isMandatory) {
      e.preventDefault();
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4 transition-opacity duration-300 animate-fadeIn">
      <div className="bg-white rounded-2xl sm:rounded-2xl shadow-[0_-10px_40px_rgba(0,0,0,0.2)] sm:shadow-2xl w-[calc(100vw-1.5rem)] max-w-md mx-auto relative flex flex-col max-h-[90vh] overflow-hidden animate-slideUp sm:animate-none">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-pink-600 p-6 sm:p-8 text-white relative z-10">
          
          {!isMandatory && (
            <button onClick={onClose} className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/80 hover:text-white transition p-2 bg-black/10 rounded-full hover:bg-black/20">
              <FaTimes size={18} />
            </button>
          )}
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {step === 'phone' && 'Login / Sign Up'}
            {step === 'details' && 'Complete Signup'}
            {step === 'otp' && 'Verify OTP'}
          </h2>
          <p className="text-white/90 text-sm sm:text-base mt-2 font-medium">
            {step === 'phone' && 'Zero Brokerage Property Deals'}
            {step === 'details' && 'Tell us your property preferences'}
            {step === 'otp' && `OTP sent to +91 ${phone}`}
          </p>
        </div>

        {/* Form Container (Scrollable if needed on small screens) */}
        <div className="p-5 sm:p-8 space-y-5 overflow-y-auto overflow-x-hidden custom-scrollbar">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg p-3 flex items-start">
              <span className="mr-2">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {step === 'phone' ? (
            <form onSubmit={handlePhoneSubmit} className="space-y-4">
              <div className="relative mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <div className="relative flex w-full min-w-0">
                  <span className="inline-flex items-center px-4 sm:px-5 rounded-l-xl border border-r-0 border-gray-300 bg-gray-50 text-gray-600 text-sm border-transparent">
                    +91
                  </span>
                  <input
                    type="tel"
                    placeholder="Enter your number"
                    value={phone}
                    onChange={handlePhoneChange}
                    className="flex-1 min-w-0 pl-4 py-3.5 sm:py-4 border border-gray-300 rounded-r-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-base"
                    required
                    autoFocus
                  />
                  {/* Flipped the icon horizontally by using scale-x-[-1] */}
                  <FaPhone className="absolute right-5 top-1/2 hidden -translate-y-1/2 text-gray-400 pointer-events-none scale-x-[-1]" />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || phone.length !== 10}
                className="w-full bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white py-3 sm:py-4 rounded-xl font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg shadow-lg shadow-red-500/30"
              >
                {loading ? 'Checking...' : 'Continue'}
              </button>
            </form>
          ) : step === 'details' ? (
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div className="text-center mb-4 sm:mb-6">
                <p className="text-sm sm:text-base text-gray-600">
                  Phone: <span className="font-semibold text-red-600">+91 {phone}</span>
                </p>
                <button
                  type="button"
                  onClick={() => setStep('phone')}
                  className="text-xs sm:text-sm text-red-600 hover:text-red-700 mt-2 font-medium"
                >
                  Change Number
                </button>
              </div>

              <div className="relative mb-5 sm:mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FaUser className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Enter your full name"
                    value={profileForm.name}
                    onChange={(e) => {
                      setProfileForm((prev) => ({ ...prev, name: e.target.value }));
                      setError('');
                    }}
                    className="w-full pl-12 pr-4 py-3.5 sm:py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-base"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price Range <span className="text-red-500">*</span></label>
                <select
                  value={profileForm.preferredPriceRange}
                  onChange={(e) => {
                    setProfileForm((prev) => ({ ...prev, preferredPriceRange: e.target.value }));
                    setError('');
                  }}
                  className="w-full px-4 py-3.5 sm:py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-base bg-white"
                  required
                >
                  <option value="">Select price range</option>
                  <option value="Below 50 Lac">Below 50 Lac</option>
                  <option value="50 Lac - 1 Cr">50 Lac - 1 Cr</option>
                  <option value="1 Cr - 2 Cr">1 Cr - 2 Cr</option>
                  <option value="2 Cr - 5 Cr">2 Cr - 5 Cr</option>
                  <option value="Above 5 Cr">Above 5 Cr</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Property Type <span className="text-red-500">*</span></label>
                <div className="relative">
                  <FaHome className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <select
                    value={profileForm.preferredPropertyType}
                    onChange={(e) => {
                      setProfileForm((prev) => ({ ...prev, preferredPropertyType: e.target.value }));
                      setError('');
                    }}
                    className="w-full pl-12 pr-4 py-3.5 sm:py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-base bg-white"
                    required
                  >
                    <option value="">Select property type</option>
                    <option value="Apartment">Apartment</option>
                    <option value="Villa">Villa</option>
                    <option value="Banglo">Banglo</option>
                    <option value="Plot">Plot</option>
                    <option value="Commercial">Commercial</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">BHK Configurations <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {bhkOptions.map((option) => {
                    const selected = profileForm.preferredBhkConfigurations.includes(option);
                    return (
                      <button
                        type="button"
                        key={option}
                        onClick={() => toggleBhkOption(option)}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${selected ? 'bg-red-500 border-red-500 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-red-300'}`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Property Location <span className="text-red-500">*</span></label>
                <div className="relative">
                  <FaMapMarkerAlt className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="e.g., Kharadi, Pune"
                    value={profileForm.preferredLocation}
                    onChange={(e) => {
                      setProfileForm((prev) => ({ ...prev, preferredLocation: e.target.value }));
                      setError('');
                    }}
                    className="w-full pl-12 pr-4 py-3.5 sm:py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-base"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amenities (Optional)</label>
                <input
                  type="text"
                  placeholder="Pool, Gym, Clubhouse"
                  value={amenitiesInput}
                  onChange={(e) => {
                    setAmenitiesInput(e.target.value);
                    setError('');
                  }}
                  className="w-full px-4 py-3.5 sm:py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-base"
                />
                <p className="text-xs text-gray-500 mt-1">Comma se separate karein. Example: Pool, Gym, Garden</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white py-3 sm:py-4 rounded-xl font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg shadow-lg shadow-red-500/30"
              >
                {loading ? 'Saving...' : 'Complete Signup'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div className="text-center mb-4 sm:mb-6">
                {isExistingUser ? (
                  <>
                    <p className="text-sm sm:text-base text-gray-600 mb-2">
                      Welcome back, <span className="font-semibold text-red-600">{name}</span>!
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setStep('phone');
                        setName('');
                        setIsExistingUser(false);
                      }}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Change Number
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-sm sm:text-base text-gray-600 mb-2">
                      OTP verify karne ke baad aap signup details fill karenge.
                    </p>
                    <button
                      type="button"
                      onClick={() => setStep('phone')}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Change Number
                    </button>
                  </>
                )}
              </div>

              <div className="relative mb-5 sm:mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  6-Digit OTP <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FaLock className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="tel"
                    placeholder="• • • • • •"
                    value={otp}
                    onChange={handleOTPChange}
                    className="w-full pl-12 pr-12 py-3.5 sm:py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-center text-2xl sm:text-3xl tracking-widest sm:tracking-[0.5em] font-semibold"
                    required
                    maxLength={6}
                    autoFocus
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white py-3 sm:py-4 rounded-xl font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg shadow-lg shadow-red-500/30 mb-4 sm:mb-6"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>

              <div className="text-center">
                {timer > 0 ? (
                  <p className="text-sm sm:text-base text-gray-600 font-medium">
                    Resend OTP in <span className="text-red-600">{timer}</span>s
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="text-sm sm:text-base text-red-600 hover:text-red-700 font-semibold disabled:opacity-50 transition"
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </form>
          )}

          <div className="pt-5 sm:pt-6 pb-2 border-t border-gray-100 text-center text-xs sm:text-sm text-gray-500 break-words">
            By continuing, you agree to our{' '}
            <Link 
              to="/terms-conditions" 
              className="text-red-600 hover:text-red-700 font-medium"
              onClick={(e) => handleLegalClick(e, '/terms-conditions')}
            >
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link 
              to="/privacy-policy" 
              className="text-red-600 hover:text-red-700 font-medium"
              onClick={(e) => handleLegalClick(e, '/privacy-policy')}
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
